/**
 * Policy — owner-level controls enforced by PolicyGate.
 *
 * Three sub-tabs at /app/policy/{rules,dnc,review}:
 *   rules  — outbound hours, throughput caps, pause toggle, recap channel,
 *            recording default, default disclosure rules.
 *   dnc    — Do-Not-Call list (phone numbers + people that should never
 *            be called for any reason).
 *   review — pending policy_overrides queue (PolicyGate hits that need
 *            human approval before the action runs).
 */
import { useState } from "react";
import { NavLink, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { policy, type UserPreferences, type DncEntry, type PolicyOverride } from "../api/client";
import { Button, Card, LiveDot, Mono, Pill } from "../components/v3";

export function Policy() {
  return (
    <div className="px-8 py-6 max-w-[1280px] mx-auto">
      <div className="mb-6">
        <Mono className="block mb-2">Policy</Mono>
        <h1 className="font-display text-3xl leading-tight">
          Rules she <span className="font-display-italic">won't break</span>.
        </h1>
        <p className="text-t2 mt-2 max-w-[58ch]">
          Hours, throughput caps, do-not-call lists, things she's allowed to
          disclose. PolicyGate fails closed — when in doubt, she defers.
        </p>
      </div>

      <SubNav />

      <div className="mt-5">
        <Routes>
          <Route index element={<Navigate to="rules" replace />} />
          <Route path="rules" element={<RulesTab />} />
          <Route path="dnc" element={<DncTab />} />
          <Route path="review" element={<ReviewTab />} />
        </Routes>
        <Outlet />
      </div>
    </div>
  );
}

function SubNav() {
  const items = [
    { to: "/app/policy/rules", label: "Rules" },
    { to: "/app/policy/dnc", label: "Do-not-call" },
    { to: "/app/policy/review", label: "Review queue" },
  ];
  return (
    <div className="flex gap-1 border-b border-line">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          className={({ isActive }) =>
            [
              "relative px-4 py-2.5 text-sm font-medium transition-colors",
              isActive ? "text-t1" : "text-t3 hover:text-t1",
            ].join(" ")
          }
        >
          {({ isActive }) => (
            <>
              {it.label}
              {isActive && (
                <span className="absolute bottom-[-1px] left-3 right-3 h-[2px] bg-amber rounded-t-sm" />
              )}
            </>
          )}
        </NavLink>
      ))}
    </div>
  );
}

// ── Rules tab ──────────────────────────────────────────────────────────────

function RulesTab() {
  const qc = useQueryClient();
  const { data: prefs, isLoading } = useQuery({
    queryKey: ["policy", "preferences"],
    queryFn: () => policy.getPreferences(),
  });

  const [dirty, setDirty] = useState<Partial<UserPreferences>>({});
  const merged = prefs ? { ...prefs, ...dirty } : null;
  const isDirty = Object.keys(dirty).length > 0;

  async function save() {
    if (!isDirty) return;
    await policy.updatePreferences(dirty);
    setDirty({});
    qc.invalidateQueries({ queryKey: ["policy", "preferences"] });
  }

  function set<K extends keyof UserPreferences>(k: K, v: UserPreferences[K]) {
    setDirty((d) => ({ ...d, [k]: v }));
  }

  if (isLoading || !merged) {
    return (
      <div className="space-y-3">
        <div className="skeleton h-32" />
        <div className="skeleton h-48" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pause control */}
      <Card padding="md" glow={merged.paused}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {merged.paused && <LiveDot tone="rose" />}
              <Mono tone={merged.paused ? "amber" : "t3"}>
                {merged.paused ? "Paused" : "Active"}
              </Mono>
            </div>
            <h2 className="font-display text-xl leading-tight mb-2">
              {merged.paused
                ? "She's paused. She won't make any outbound calls."
                : "She'll handle calls per the rules below."}
            </h2>
            {merged.paused && merged.paused_reason && (
              <p className="text-t2 text-sm italic">
                Reason: {merged.paused_reason}
              </p>
            )}
          </div>
          <Button
            variant={merged.paused ? "amber" : "ghost"}
            size="sm"
            onClick={() => set("paused", !merged.paused)}
          >
            {merged.paused ? "Resume" : "Pause"}
          </Button>
        </div>
      </Card>

      {/* Outbound hours */}
      <Card padding="md">
        <Mono className="block mb-3">Outbound hours</Mono>
        <p className="text-t2 text-sm mb-4 max-w-[58ch]">
          She won't place outbound calls outside this window (in the timezone
          below). She may still take inbound at any time.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Start">
            <TimeInput
              value={merged.outbound_hours_start}
              onChange={(v) => set("outbound_hours_start", v)}
            />
          </Field>
          <Field label="End">
            <TimeInput
              value={merged.outbound_hours_end}
              onChange={(v) => set("outbound_hours_end", v)}
            />
          </Field>
          <Field label="Timezone">
            <TextInput
              value={merged.outbound_local_tz}
              onChange={(v) => set("outbound_local_tz", v)}
              placeholder="America/Los_Angeles"
            />
          </Field>
        </div>
      </Card>

      {/* Throughput caps */}
      <Card padding="md">
        <Mono className="block mb-3">Throughput</Mono>
        <p className="text-t2 text-sm mb-4 max-w-[58ch]">
          How often she's allowed to contact the same person — protects against
          loops, accidents, and looking pushy.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Max per contact / day">
            <NumberInput
              value={merged.default_max_per_contact_per_day}
              onChange={(v) => set("default_max_per_contact_per_day", v)}
            />
          </Field>
          <Field label="Max per contact / week">
            <NumberInput
              value={merged.default_max_per_contact_per_week}
              onChange={(v) => set("default_max_per_contact_per_week", v)}
            />
          </Field>
        </div>
      </Card>

      {/* Channel + recording defaults */}
      <Card padding="md">
        <Mono className="block mb-3">Defaults</Mono>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Recap channel">
            <SelectInput
              value={merged.default_recap_channel}
              onChange={(v) =>
                set(
                  "default_recap_channel",
                  v as UserPreferences["default_recap_channel"],
                )
              }
              options={[
                { value: "telegram", label: "Telegram" },
                { value: "sms", label: "SMS" },
                { value: "email", label: "Email" },
                { value: "none", label: "None — no recap" },
              ]}
            />
          </Field>
          <Field label="Recording on by default">
            <Toggle
              checked={merged.recording_default_enabled}
              onChange={(v) => set("recording_default_enabled", v)}
              labelOn="On (when consent allows)"
              labelOff="Off"
            />
          </Field>
        </div>
      </Card>

      {/* Disclosure rules — read-only stub */}
      <Card padding="md" className="border-dashed">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Mono className="block mb-2">Disclosure rules</Mono>
            <h3 className="font-medium text-t1 mb-1">
              What she can and can't share on calls
            </h3>
            <p className="text-t3 text-sm max-w-[58ch] leading-relaxed">
              Per-call tool scoping (allowed_tools), topic-level redactions
              ("never mention my address"), and contact-specific disclosure
              limits surface here once the policy_overrides workflow lands a
              richer schema.
            </p>
          </div>
          <Pill tone="warn">Soon</Pill>
        </div>
      </Card>

      {/* Save bar */}
      {isDirty && (
        <div className="sticky bottom-4 mt-6">
          <Card padding="md" glow>
            <div className="flex items-center justify-between gap-4">
              <Mono tone="amber">Unsaved changes</Mono>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setDirty({})}>
                  Discard
                </Button>
                <Button variant="amber" size="sm" onClick={save}>
                  Save changes
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── DNC tab ───────────────────────────────────────────────────────────────

function DncTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["policy", "dnc"],
    queryFn: () => policy.listDnc(),
  });

  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [adding, setAdding] = useState(false);

  async function add() {
    if (!phone.trim()) return;
    setAdding(true);
    try {
      await policy.addDnc({
        phone_number: phone.trim(),
        reason: reason.trim() || undefined,
      });
      setPhone("");
      setReason("");
      qc.invalidateQueries({ queryKey: ["policy", "dnc"] });
    } finally {
      setAdding(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm("Remove this DNC entry?")) return;
    await policy.removeDnc(id);
    qc.invalidateQueries({ queryKey: ["policy", "dnc"] });
  }

  return (
    <div className="space-y-4">
      <Card padding="md">
        <Mono className="block mb-3">Add to do-not-call</Mono>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <Field label="Phone (E.164)">
            <TextInput
              value={phone}
              onChange={setPhone}
              placeholder="+15551234567"
            />
          </Field>
          <Field label="Reason (optional)">
            <TextInput
              value={reason}
              onChange={setReason}
              placeholder="e.g. they asked to be removed"
            />
          </Field>
          <Button
            variant="amber"
            size="md"
            onClick={add}
            disabled={!phone.trim() || adding}
          >
            {adding ? "Adding…" : "Add"}
          </Button>
        </div>
      </Card>

      <Card padding="none" className="overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_120px_120px_60px] gap-3 px-5 py-3 border-b border-line-soft">
          <Mono>Number / Person</Mono>
          <Mono>Reason</Mono>
          <Mono>Source</Mono>
          <Mono>Added</Mono>
          <span />
        </div>
        {isLoading ? (
          <div className="p-5 space-y-2">
            <div className="skeleton h-10" />
            <div className="skeleton h-10" />
          </div>
        ) : !data?.entries || data.entries.length === 0 ? (
          <div className="p-12 text-center">
            <div className="font-display text-xl text-t2">No DNC entries</div>
            <div className="text-t3 text-sm mt-2 max-w-[42ch] mx-auto">
              Numbers Vooice will refuse to dial. She'll also auto-add anyone
              who asks to be removed mid-call.
            </div>
          </div>
        ) : (
          data.entries.map((e: DncEntry) => (
            <div
              key={e.id}
              className="grid grid-cols-[1fr_1fr_120px_120px_60px] gap-3 px-5 py-3 border-b border-line-soft last:border-b-0 items-center"
            >
              <span className="font-mono text-sm text-t1 truncate">
                {e.phone_number ?? e.person_name ?? e.person_id ?? "—"}
              </span>
              <span className="text-t2 text-sm truncate">
                {e.reason ?? <span className="text-t4">—</span>}
              </span>
              <Pill tone={e.source === "manual" ? "neutral" : "warn"}>
                {e.source.replace("_", " ")}
              </Pill>
              <Mono>{new Date(e.created_at).toLocaleDateString()}</Mono>
              <button
                onClick={() => remove(e.id)}
                className="text-t4 hover:text-rose text-sm transition-colors"
                aria-label="Remove"
              >
                ×
              </button>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}

// ── Review tab ────────────────────────────────────────────────────────────

function ReviewTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["policy", "overrides"],
    queryFn: () => policy.listOverrides(),
    refetchInterval: 5000,
  });

  async function approve(id: string) {
    await policy.approveOverride(id);
    qc.invalidateQueries({ queryKey: ["policy", "overrides"] });
  }
  async function deny(id: string) {
    const reason = window.prompt("Why? (optional)") ?? undefined;
    await policy.denyOverride(id, reason);
    qc.invalidateQueries({ queryKey: ["policy", "overrides"] });
  }

  if (isLoading) return <div className="skeleton h-32" />;

  if (!data?.overrides || data.overrides.length === 0) {
    return (
      <Card padding="lg" className="text-center">
        <div className="font-display text-xl text-t2">Nothing to review</div>
        <p className="text-t3 text-sm mt-2 max-w-[42ch] mx-auto">
          When PolicyGate flags an action that needs your approval, it lands
          here with a 5-minute decision window before auto-expiring as denied.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {data.overrides.map((o: PolicyOverride) => (
        <Card key={o.id} padding="md" glow>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <LiveDot tone="amber" />
                <Mono tone="amber">{o.action}</Mono>
                <Mono>
                  Expires{" "}
                  {Math.max(
                    0,
                    Math.round(
                      (new Date(o.expires_at).getTime() - Date.now()) / 1000,
                    ),
                  )}
                  s
                </Mono>
              </div>
              <pre className="text-xs text-t2 bg-bg-2 p-2 rounded-md overflow-x-auto font-mono">
                {JSON.stringify(o.params_json, null, 2)}
              </pre>
              {o.reasons && o.reasons.length > 0 && (
                <div className="mt-2">
                  <Mono className="block mb-1">Reasons</Mono>
                  <ul className="text-sm text-t2 space-y-0.5">
                    {o.reasons.map((r, i) => (
                      <li key={i}>· {r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button variant="amber" size="sm" onClick={() => approve(o.id)}>
                Approve
              </Button>
              <Button variant="ghost" size="sm" onClick={() => deny(o.id)}>
                Deny
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ── Form primitives ───────────────────────────────────────────────────────

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Mono className="block mb-2">{label}</Mono>
      {children}
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-bg-2 border border-line rounded-md text-sm text-t1 placeholder:text-t4 focus:outline-none focus:border-amber/40 transition-colors";

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (s: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputCls}
    />
  );
}

function NumberInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <input
      type="number"
      min={0}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
      className={inputCls}
    />
  );
}

function TimeInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (s: string) => void;
}) {
  // user_preferences stores TIME (HH:MM:SS); HTML <input type="time"> wants HH:MM.
  const trimmed = value.length > 5 ? value.slice(0, 5) : value;
  return (
    <input
      type="time"
      value={trimmed}
      onChange={(e) => onChange(`${e.target.value}:00`)}
      className={inputCls}
    />
  );
}

function SelectInput({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (s: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Toggle({
  checked,
  onChange,
  labelOn,
  labelOff,
}: {
  checked: boolean;
  onChange: (b: boolean) => void;
  labelOn: string;
  labelOff: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={[
        "w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md border text-sm transition-colors",
        checked
          ? "bg-amber/10 border-amber/40 text-amber"
          : "bg-bg-2 border-line text-t2 hover:border-t4",
      ].join(" ")}
    >
      <span>{checked ? labelOn : labelOff}</span>
      <span
        className={[
          "size-4 rounded-full border transition-colors",
          checked ? "bg-amber border-amber" : "border-line",
        ].join(" ")}
        aria-hidden="true"
      />
    </button>
  );
}
